/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// FIRM QUESTIONS (v1_833). The questions the firm's policy questionnaire
// (section 14, "Asked of every client regardless of risk") says are asked of
// every client. Signing writes them to compliance_config.firmQuestions. The
// client answers them on the documents first checklist, in the interview and on
// a return visit; each answer is stored on the case under
// collected_data.firmQuestions as { question, answer, at, via }, written only by
// api/firm-question-answer.js (a wholesale save can neither set nor drop one).
// An unanswered question is named in Next action and added to Draft Client
// Request; it is never a sign off blocker. Pure.

export const FIRM_QUESTIONS_KEY = 'firmQuestions';
export const FIRM_ANSWER_MAX = 2000;

const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
// Two spellings of one question match (case, spacing, punctuation).
export const questionKey = (q) => clean(q).toLowerCase().replace(/[^a-z0-9 ]+/g, '').replace(/\s+/g, ' ').trim();

// The firm's list, from compliance_config (or a list already read from it).
export function firmQuestionsOf(src) {
  const raw = Array.isArray(src) ? src : (src && Array.isArray(src.firmQuestions) ? src.firmQuestions : []);
  const seen = new Set(), out = [];
  for (const q of raw) { const t = clean(q), k = questionKey(t); if (t && k && !seen.has(k)) { seen.add(k); out.push(t); } }
  return out;
}

// The answers on the case.
export function firmAnswersOf(collected) {
  const raw = collected && Array.isArray(collected[FIRM_QUESTIONS_KEY]) ? collected[FIRM_QUESTIONS_KEY] : [];
  return raw.filter(a => a && clean(a.question) && clean(a.answer)).map(a => ({ question: clean(a.question), answer: String(a.answer).trim().slice(0, FIRM_ANSWER_MAX), at: a.at || null, via: a.via || null, ...(a.by ? { by: clean(a.by) } : {}), ...(a.note ? { note: String(a.note).trim() } : {}) }));
}

// One row per firm question, in the firm's order, with its answer if given;
// then any answer to a question the firm has since taken off its list.
export function firmQuestionRows(questions, collected) {
  const answers = firmAnswersOf(collected);
  const byKey = new Map(answers.map(a => [questionKey(a.question), a]));
  const list = firmQuestionsOf(questions);
  const keys = new Set(list.map(questionKey));
  const rows = list.map(q => { const a = byKey.get(questionKey(q)); return a ? { ...a, question: q, answered: true } : { question: q, answer: null, at: null, via: null, answered: false }; });
  for (const a of answers) if (!keys.has(questionKey(a.question))) rows.push({ ...a, answered: true, retired: true });
  return rows;
}

export const unansweredFirmQuestions = (questions, collected) => firmQuestionRows(questions, collected).filter(r => !r.answered).map(r => r.question);

// Record one answer (a later answer to the same question replaces the earlier).
// v1_833b: extra carries an officer's record ({ by, note: where it was given }).
export function withFirmAnswer(collected, question, answer, via, at = new Date().toISOString(), extra = {}) {
  const list = firmAnswersOf(collected).filter(a => questionKey(a.question) !== questionKey(question));
  list.push({ question: clean(question), answer: String(answer).trim().slice(0, FIRM_ANSWER_MAX), at, via, ...(extra.by ? { by: clean(extra.by) } : {}), ...(extra.note ? { note: String(extra.note).trim() } : {}) });
  return list;
}

// The words shown with an answer, for the case and the Audit Record.
export const FIRM_ANSWER_VIA = Object.freeze({ checklist: 'on the documents checklist', interview: 'in the interview', return: 'on a return visit', officer: 'recorded by an officer' });

// v1_833b: how an answer is described on the case and in the Audit Record.
export function firmAnswerSource(row, fmtDate = (t) => { try { return t ? new Date(t).toLocaleDateString('en-GB') : ''; } catch (_) { return ''; } }) {
  if (!row || !row.answered) return '';
  const when = fmtDate(row.at);
  if (row.via === 'officer') return `recorded by ${row.by || 'an officer'}${when ? ' on ' + when : ''}${row.note ? '; given ' + row.note.replace(/^given\s+/i, '') : ''}`;
  return `answered ${FIRM_ANSWER_VIA[row.via] || 'by the client'}${when ? ' on ' + when : ''}`;
}

// Draft Client Request: the line added for each unanswered question.
export const firmQuestionDraftLine = (q) => `A question we ask every client: ${clean(q)}`;

// The interview: what Alex is told.
export function firmQuestionsPrompt(questions, collected, firmName) {
  const rows = firmQuestionRows(questions, collected).filter(r => !r.retired);
  if (!rows.length) return '';
  const who = clean(firmName) || 'The firm';
  return '\n\nQUESTIONS THE FIRM ASKS EVERY CLIENT (' + who + '\'s policy, section 14). The client answers each one in the "Questions we ask every client" box shown below the chat, which records the answer against the question. Before the summary, ask each unanswered one, in these words, and invite the client to answer it in that box. Do not ask one already answered, and do not answer on the client\'s behalf.\n'
    + rows.map((r, i) => `${i + 1}. ${r.question} ${r.answered ? '(answered: ' + r.answer.slice(0, 200) + ')' : '(not yet answered)'}`).join('\n');
}
